import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CBadge, CButton, CCol, CFormInput, CFormLabel, CRow } from "@coreui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslationContext } from "@/context/TranslationContext";
import { breadcrumb_key } from "@/constants/translation/common.translation";
import { subscribedUser } from "@/constants/translation/subscribedUser.translation";
import { subscriptionPlan } from "@/constants/translation/subscriptionPlan.translation";
import { masters } from "@/libs/routes";
import { setFormatDate } from "@/utils/dateFormate";
import { useSubscribedUserList } from "@/hooks/useSubscribedUser";
import { useSubscriptionPlanList } from "@/hooks/useSubscriptionPlan";
import { TNTable } from "@/components";
import CustomDropdown from "@/components/CustomDropdown";
import constants from "@/libs/constant";

const { dateTypes } = constants;

const STATUS_FILTER_VALUES = ["all", "active", "expired", "cancelled"];
const USER_TYPE_FILTER_VALUES = ["all", constants.userType.normalUser, constants.userType.freelancerUser];

// API responses pass every value through the app's shared encryption round-trip, which
// stringifies booleans (true -> "true", false -> "false"), matching the convention already
// used for is_read in src/pages/notification/list/index.js. Native JS truthiness on the raw
// value is wrong here since the string "false" is truthy.
const isAutoRenewTrue = (value) => value === true || value === constants.status.apiStatus.true;

const getSubscriptionStatusBadgeClass = (status) => {
  switch (status) {
    case "active":
      return "success";
    case "cancelled":
      return "danger";
    case "expired":
      return "warning";
    default:
      return "secondary";
  }
};

const SubscribedUserList = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();

  const [subscribedUserList, setSubscribedUserList] = useState([]);
  const [paginationData, setPaginationData] = useState(null);

  const [currentPage, setCurrentPage] = useState(constants.pagination.defaultPage);
  const [searchText, setSearchText] = useState("");
  const [tempSearchText, setTempSearchText] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [userTypeFilter, setUserTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [tableKey, setTableKey] = useState(0);
  const [sortField, setSortField] = useState("purchase_date");
  const [sortOrder, setSortOrder] = useState(constants.sort.order.desc);

  const searchTimeout = useRef(null);
  const isFiltering = useRef(false);

  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setTempSearchText(searchValue);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      isFiltering.current = true;
      setSearchText(searchValue);
      setCurrentPage(1);
      setTableKey((prev) => prev + 1);

      setTimeout(() => {
        isFiltering.current = false;
      }, 200);
    }, 400);
  };

  const handleFilterChange = (setter) => (e) => {
    isFiltering.current = true;
    setter(e.target.value);
    setCurrentPage(1);
    setTableKey((prev) => prev + 1);

    setTimeout(() => {
      isFiltering.current = false;
    }, 200);
  };

  const handleSort = (field) => {
    isFiltering.current = true;

    if (sortField === field) {
      setSortOrder(sortOrder === constants.sort.order.asc ? constants.sort.order.desc : constants.sort.order.asc);
    } else {
      setSortField(field);
      setSortOrder(constants.sort.order.asc);
    }
    setCurrentPage(1);
    setTableKey((prev) => prev + 1);

    setTimeout(() => {
      isFiltering.current = false;
    }, 200);
  };

  const handleDateChange = (dateType, value) => {
    isFiltering.current = true;
    if (dateType === dateTypes.start) {
      setStartDate(value);
      if (endDate && value && endDate < value) {
        setEndDate(value);
      }
    } else {
      if (value && value < startDate) {
        setEndDate(startDate);
      } else {
        setEndDate(value);
      }
    }
    setCurrentPage(1);
    setTableKey((prev) => prev + 1);

    setTimeout(() => {
      isFiltering.current = false;
    }, 200);
  };

  const handleClearFilter = () => {
    isFiltering.current = true;

    setSearchText("");
    setTempSearchText("");
    setPlanFilter("");
    setUserTypeFilter("all");
    setStatusFilter("all");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
    setTableKey((prev) => prev + 1);

    setTimeout(() => {
      isFiltering.current = false;
    }, 200);
  };

  const {
    data: listData,
    isLoading,
  } = useSubscribedUserList({
    page: currentPage,
    limit: constants.pagination.defaultPageSize,
    ...(searchText.trim() && { search: searchText.trim() }),
    ...(planFilter && { plan_id: planFilter }),
    ...(userTypeFilter && userTypeFilter !== "all" && { user_type: userTypeFilter }),
    ...(statusFilter && statusFilter !== "all" && { status: statusFilter }),
    ...(startDate && { purchase_date_from: startDate }),
    ...(endDate && { purchase_date_to: endDate }),
    sort_by: sortField,
    order_by: sortOrder,
  });

  // Plan Name filter options come from the existing subscription plan list endpoint —
  // no separate plan-lookup endpoint is built for this feature.
  const { data: planListData } = useSubscriptionPlanList({ limit: 100, status: "active" });

  useEffect(() => {
    if (listData) {
      setSubscribedUserList(listData.data?.subscribedUserList || []);
      setPaginationData(listData.data?.pagination);
    }
  }, [listData]);

  const planOptions = useMemo(() => {
    const options = [{ value: "", label: t(subscribedUser.list.plan_filter_placeholder) }];
    (planListData?.data?.subscriptionPlanList || []).forEach((plan) => {
      options.push({ value: plan.uuid, label: plan.name });
    });
    return options;
  }, [planListData, t]);

  const userTypeOptions = useMemo(
    () =>
      USER_TYPE_FILTER_VALUES.map((value) => ({
        value,
        label:
          value === "all"
            ? t(subscribedUser.list.user_type_filter.all)
            : t(subscriptionPlan.user_type[value]),
      })),
    [t],
  );

  const statusOptions = useMemo(
    () =>
      STATUS_FILTER_VALUES.map((value) => ({
        value,
        label: t(subscribedUser.list.status_filter[value]),
      })),
    [t],
  );

  const handleViewClick = (subscriptionUuid) => {
    navigate(masters.subscribedUsers.view.replace(":id", subscriptionUuid));
  };

  const sortableHeader = (label, field) => (
    <button
      type="button"
      className="table-sort-button"
      onClick={(event) => {
        event.stopPropagation();
        handleSort(field);
      }}
    >
      {label}
    </button>
  );

  const columns = useMemo(
    () => [
      {
        id: "purchase_date",
        accessorKey: "purchase_date",
        sortField: "purchase_date",
        header: sortableHeader(t(subscribedUser.list.table.purchase_date_col_heading), "purchase_date"),
        enableSorting: true,
        cell: ({ getValue }) => <span>{getValue() ? setFormatDate(getValue()) : t(subscribedUser.list.defaults.na)}</span>,
      },
      {
        id: "user_name",
        accessorKey: "full_name",
        sortField: "user_name",
        header: sortableHeader(t(subscribedUser.list.table.user_name_col_heading), "user_name"),
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div>{row.original.full_name || t(subscribedUser.list.defaults.na)}</div>
            {row.original.user_name && (
              <div className="text-muted small">@{row.original.user_name}</div>
            )}
          </div>
        ),
      },
      {
        id: "email",
        accessorKey: "email",
        sortField: "email",
        header: sortableHeader(t(subscribedUser.list.table.email_col_heading), "email"),
        enableSorting: true,
        cell: ({ getValue }) => <span>{getValue() || t(subscribedUser.list.defaults.na)}</span>,
      },
      {
        id: "plan_name",
        accessorKey: "plan_name",
        sortField: "plan_name",
        header: sortableHeader(t(subscribedUser.list.table.plan_name_col_heading), "plan_name"),
        enableSorting: true,
        cell: ({ getValue }) => <span>{getValue() || t(subscribedUser.list.defaults.na)}</span>,
      },
      {
        id: "amount_paid",
        accessorKey: "amount_paid",
        sortField: "amount_paid",
        header: sortableHeader(t(subscribedUser.list.table.amount_paid_col_heading), "amount_paid"),
        enableSorting: true,
        cell: ({ getValue }) => {
          const value = getValue();
          return <span>{value != null ? `$${value}` : t(subscribedUser.list.defaults.na)}</span>;
        },
      },
      {
        id: "expiry_date",
        accessorKey: "expiry_date",
        sortField: "expiry_date",
        header: sortableHeader(t(subscribedUser.list.table.expiry_date_col_heading), "expiry_date"),
        enableSorting: true,
        cell: ({ getValue }) => <span>{getValue() ? setFormatDate(getValue()) : t(subscribedUser.list.defaults.na)}</span>,
      },
      {
        id: "auto_renew",
        accessorKey: "auto_renew",
        header: t(subscribedUser.list.table.auto_renew_col_heading),
        cell: ({ getValue }) => {
          const isAutoRenew = isAutoRenewTrue(getValue());
          return (
            <CBadge color={isAutoRenew ? "success" : "secondary"}>
              {isAutoRenew ? t(subscribedUser.list.auto_renew.yes) : t(subscribedUser.list.auto_renew.no)}
            </CBadge>
          );
        },
      },
      {
        id: "status",
        accessorKey: "status",
        sortField: "status",
        header: sortableHeader(t(subscribedUser.list.table.status_col_heading), "status"),
        enableSorting: true,
        cell: ({ getValue }) => {
          const value = getValue();
          return (
            <CBadge color={getSubscriptionStatusBadgeClass(value)}>
              {t(subscribedUser.list.status_filter[value]) || value}
            </CBadge>
          );
        },
      },
      {
        id: "action",
        accessorKey: "subscription_uuid",
        header: t(subscribedUser.list.table.action_col_heading),
        cell: ({ row }) => (
          <div className="d-flex justify-content-center gap-3">
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="view-tooltip">{t(subscribedUser.list.view_tooltip)}</Tooltip>}
            >
              <CButton
                onClick={() => handleViewClick(row.original.subscription_uuid)}
                style={{
                  backgroundColor: "#212631",
                  borderColor: "#212631",
                  color: "white",
                  padding: "0.25rem 0.5rem",
                  fontSize: "0.875rem",
                }}
              >
                <FontAwesomeIcon icon={faEye} />
              </CButton>
            </OverlayTrigger>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, sortField, sortOrder],
  );

  return (
    <>
      <CRow className="mb-4">
        <CCol lg={12} className="d-flex gap-2 justify-content-end flex-wrap">
          <CFormInput
            placeholder={t(subscribedUser.list.search_placeholder)}
            onChange={handleSearch}
            value={tempSearchText}
            className="form-control-sm"
            style={{ maxWidth: "260px" }}
          />

          <CustomDropdown
            className="form-control-sm"
            style={{ maxWidth: "200px", backgroundColor: "#212631", borderColor: "#212631", color: "white" }}
            value={planFilter}
            onChange={handleFilterChange(setPlanFilter)}
            options={planOptions}
            placeholder={t(subscribedUser.list.plan_filter_placeholder)}
          />

          <CustomDropdown
            className="form-control-sm"
            style={{ maxWidth: "200px", backgroundColor: "#212631", borderColor: "#212631", color: "white" }}
            value={userTypeFilter}
            onChange={handleFilterChange(setUserTypeFilter)}
            options={userTypeOptions}
          />

          <CustomDropdown
            className="form-control-sm"
            style={{ maxWidth: "200px", backgroundColor: "#212631", borderColor: "#212631", color: "white" }}
            value={statusFilter}
            onChange={handleFilterChange(setStatusFilter)}
            options={statusOptions}
          />

          <div className="d-flex gap-2 align-items-center">
            <CFormLabel htmlFor="startDate" className="m-0">
              {t(subscribedUser.list.date_from_placeholder)}
            </CFormLabel>
            <CFormInput
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => handleDateChange(dateTypes.start, e.target.value)}
              className="form-control"
              style={{ width: "155px", height: "45px" }}
            />
          </div>

          <div className="d-flex gap-2 align-items-center">
            <CFormLabel htmlFor="endDate" className="m-0">
              {t(subscribedUser.list.date_to_placeholder)}
            </CFormLabel>
            <CFormInput
              type="date"
              id="endDate"
              value={endDate}
              min={startDate}
              onChange={(e) => handleDateChange(dateTypes.end, e.target.value)}
              className="form-control"
              style={{ width: "155px", height: "45px" }}
            />
          </div>

          {(tempSearchText ||
            planFilter ||
            userTypeFilter !== "all" ||
            statusFilter !== "all" ||
            startDate ||
            endDate) && (
            <CButton color="primary" type="button" onClick={handleClearFilter} style={{ height: "45px" }}>
              {t(subscribedUser.list.clear_filter_btn_text)}
            </CButton>
          )}
        </CCol>
      </CRow>

      <TNTable
        key={tableKey}
        columns={columns}
        data={subscribedUserList}
        paginationData={paginationData}
        onSelectPage={(pageIndex) => {
          if (isFiltering.current) {
            return;
          }
          setCurrentPage(pageIndex + 1);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        t={t}
        pageIndexGet={currentPage - 1}
        isLoading={isLoading}
        sortField={sortField}
        sortOrder={sortOrder}
      />
    </>
  );
};

SubscribedUserList.breadCrumbArray = [
  {
    name: breadcrumb_key.masters?.subscribedUser?.list,
    pathname: masters.subscribedUsers.list,
    active: true,
  },
];

export default SubscribedUserList;
